import { useLang } from "@/context/LanguageContext";
import { ASSETS } from "@/data/manoly";
import { HERO } from "@/constants/testIds";
import { ArrowDown, ArrowUpRight } from "lucide-react";

export const Hero = () => {
  const { tr } = useLang();
  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative w-full overflow-hidden bg-[color:var(--paris-navy)] flex flex-col"
    >
      {/* === Mobile: foto arriba, texto abajo (no se tapa la cara) === */}
      <div className="md:hidden relative w-full h-[62svh] min-h-[380px] overflow-hidden">
        <img
          src={ASSETS.heroArmsCrossed}
          srcSet={ASSETS.heroArmsCrossedMobileSrcSet}
          sizes="100vw"
          alt="Manoly Baquerizo con la camiseta oficial del Paris FC"
          width="1080"
          height="1440"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-[center_top]"
        />
      </div>
      <div className="md:hidden bg-[color:var(--paris-navy)] px-5 pt-8 pb-14">
        <div className="flex flex-wrap items-center justify-between gap-3 text-[10px] tracking-[0.24em] uppercase mb-6">
          <span className="font-semibold text-[color:var(--cream)]">Mercato 26/27</span>
          <span className="font-semibold text-[color:var(--cream)]/85">{tr.hero.badge}</span>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-8 bg-[color:var(--cream)]" />
          <span className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--cream)] font-semibold">
            {tr.hero.new}
          </span>
        </div>

        <h1 className="text-[color:var(--cream)] leading-[1.05] m-0">
          <span
            className="block uppercase"
            style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, letterSpacing: "0.42em", fontSize: "clamp(1.2rem, 7vw, 3rem)", lineHeight: 1 }}
          >
            Manoly
          </span>
          <span
            className="block uppercase mt-2 text-[color:var(--cream)]"
            style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, letterSpacing: "0.42em", fontSize: "clamp(1.2rem, 7vw, 3rem)", lineHeight: 1 }}
          >
            Baquerizo
          </span>
        </h1>

        <p className="mt-6 text-sm text-[color:var(--cream)] leading-relaxed">
          {tr.hero.tagline}
        </p>

        <div className="mt-7 flex flex-col gap-3">
          <a href="#bio" data-testid={HERO.cta} className="btn-pill justify-center">
            <span>{tr.hero.cta}</span>
            <ArrowUpRight size={16} strokeWidth={1.75} />
          </a>
          <a href="#contact" className="btn-pill btn-pill-solid-navy justify-center">
            {tr.nav.contact}
          </a>
        </div>
      </div>

      {/* === Desktop/Tablet: overlay sobre la foto === */}
      <div className="hidden md:block relative min-h-[100svh] w-full overflow-hidden">
        <div className="absolute inset-0 deco">
          <img
            src={ASSETS.heroArmsCrossed}
            srcSet={ASSETS.heroArmsCrossedDesktopSrcSet}
            sizes="100vw"
            alt="Manoly Baquerizo con la camiseta oficial del Paris FC"
            width="1500"
            height="2000"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-[center_top]"
          />
          <div className="absolute inset-0 hero-overlay-left deco" />
        </div>

        <div className="relative z-10 pt-32 px-10 max-w-[1440px] mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] tracking-[0.24em] uppercase">
            <span className="font-semibold text-[color:var(--cream)] bg-[color:var(--paris-navy)] px-3 py-1.5 rounded-sm">
              Mercato 26/27
            </span>
            <span className="font-semibold text-[color:var(--cream)] bg-[color:var(--paris-navy)] px-3 py-1.5 rounded-sm">
              {tr.hero.badge}
            </span>
          </div>
        </div>

        <div className="relative z-10 px-10 max-w-[1440px] mx-auto mt-20 pb-40">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[color:var(--sky)]" />
              <span className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--sky)] font-semibold">
                {tr.hero.new}
              </span>
            </div>

            <h1 className="text-[color:var(--cream)] leading-[1.05] m-0">
              <span
                className="block uppercase"
                style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, letterSpacing: "0.42em", fontSize: "clamp(2rem, 7vw, 4.8rem)", lineHeight: 1 }}
              >
                Manoly
              </span>
              <span
                className="block uppercase mt-4 text-[color:var(--cream)]"
                style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, letterSpacing: "0.42em", fontSize: "clamp(2rem, 7vw, 4.8rem)", lineHeight: 1 }}
              >
                Baquerizo
              </span>
            </h1>

            <div className="mt-9 max-w-xl bg-[color:var(--paris-navy)]/85 border border-[color:var(--sky)]/25 rounded-lg px-6 py-5">
              <p className="text-base text-[color:var(--cream)] leading-relaxed">
                {tr.hero.tagline}
              </p>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href="#bio" data-testid={HERO.cta + "-d"} className="btn-pill">
                <span>{tr.hero.cta}</span>
                <ArrowUpRight size={16} strokeWidth={1.75} />
              </a>
              <a href="#contact" className="btn-pill btn-pill-solid-navy">
                {tr.nav.contact}
              </a>
            </div>
          </div>
        </div>

        <a
          href="#bio"
          data-testid={HERO.scroll}
          className="absolute bottom-8 left-10 z-10 flex items-center gap-3 group"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--cream)] group-hover:text-[color:var(--sky)] font-semibold transition-colors">
            {tr.hero.scroll}
          </span>
          <span className="w-10 h-px bg-[color:var(--cream)] group-hover:bg-[color:var(--sky)] transition-colors" />
          <ArrowDown size={14} strokeWidth={1.75} className="text-[color:var(--cream)] group-hover:text-[color:var(--sky)] transition-colors" />
        </a>

        {/* Corner badge — Paris Navy pill with cream text */}
        <div className="absolute bottom-8 right-10 z-10 flex flex-col items-end gap-1.5">
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[color:var(--cream)] bg-[color:var(--paris-navy)] px-3 py-1.5 rounded-sm">
            {tr.hero.newcomer}
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--cream)] bg-[color:var(--paris-navy)] px-2.5 py-1 rounded-sm font-semibold">
            Paris FC — 2026/27
          </span>
        </div>
      </div>
    </section>
  );
};
